import { createAsyncThunk } from '@reduxjs/toolkit';

import api, { handleApiError } from 'state/api';

export const getSkuCatalogSyncStatusApi = createAsyncThunk(
  'skuManagement/getCatalogSyncStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('sku-management/catalog/sync-status');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || 'Could not load catalog sync status',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getSkuCatalogSyncStatusApi', error, rejectWithValue);
    }
  },
);

export const syncSkuCatalogFromListingsApi = createAsyncThunk(
  'skuManagement/syncCatalogFromListings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('sku-management/catalog/sync-from-listings');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || body.error || 'Could not start SKU catalog sync',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('syncSkuCatalogFromListingsApi', error, rejectWithValue);
    }
  },
);

export const getSkuManagementSettingsApi = createAsyncThunk(
  'skuManagement/getSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('sku-management/settings');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || 'Could not load SKU management settings',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getSkuManagementSettingsApi', error, rejectWithValue);
    }
  },
);

export const updateSkuManagementSettingsApi = createAsyncThunk(
  'skuManagement/updateSettings',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put('sku-management/settings', payload);
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || body.error || 'Could not save settings',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('updateSkuManagementSettingsApi', error, rejectWithValue);
    }
  },
);

export const startEbayItemDetailsEnrichmentApi = createAsyncThunk(
  'skuManagement/startEbayEnrichment',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('sku-management/enrichment/ebay-item-details');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || body.error || 'Could not start eBay enrichment',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('startEbayItemDetailsEnrichmentApi', error, rejectWithValue);
    }
  },
);

export const getEbayEnrichmentStatusApi = createAsyncThunk(
  'skuManagement/getEbayEnrichmentStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('sku-management/enrichment/ebay-status');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || 'Could not load eBay enrichment status',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getEbayEnrichmentStatusApi', error, rejectWithValue);
    }
  },
);

export const startEbaySkuDataSyncApi = createAsyncThunk(
  'skuManagement/startEbaySkuDataSync',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('sku-management/sync/ebay-sku-data');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || body.error || 'Could not start eBay SKU data sync',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('startEbaySkuDataSyncApi', error, rejectWithValue);
    }
  },
);

export const resetEbaySkuDataSyncMarkersApi = createAsyncThunk(
  'skuManagement/resetEbaySkuDataSyncMarkers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('sku-management/sync/ebay-sku-data/reset-markers');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || body.error || 'Could not reset sync markers',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('resetEbaySkuDataSyncMarkersApi', error, rejectWithValue);
    }
  },
);

export const getEbaySkuDataSyncStatusApi = createAsyncThunk(
  'skuManagement/getEbaySkuDataSyncStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('sku-management/sync/ebay-sku-data-status');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || 'Could not load eBay SKU data sync status',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getEbaySkuDataSyncStatusApi', error, rejectWithValue);
    }
  },
);

/** Must match server `SKU_MANAGEMENT_RESET_CONFIRMATION_PHRASE`. */
export const SKU_MANAGEMENT_RESET_CONFIRMATION_PHRASE = 'DELETE ALL SKU DATA';

export const resetAllSkuManagementDataApi = createAsyncThunk(
  'skuManagement/resetAllData',
  async ({ confirmationPhrase }, { rejectWithValue }) => {
    try {
      const response = await api.post('sku-management/reset/all', {
        confirmationPhrase,
      });
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || body.error || 'Could not reset SKU management data',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('resetAllSkuManagementDataApi', error, rejectWithValue);
    }
  },
);

export const getSkuItemsOverviewApi = createAsyncThunk(
  'skuManagement/getItemsOverview',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('sku-management/items/overview');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || 'Could not load SKU overview',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getSkuItemsOverviewApi', error, rejectWithValue);
    }
  },
);

export const getDataProfileStatsApi = createAsyncThunk(
  'skuManagement/getDataProfileStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('sku-management/data-profile/stats');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || 'Could not load data profile stats',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getDataProfileStatsApi', error, rejectWithValue);
    }
  },
);

export const startDataProfileRecalcApi = createAsyncThunk(
  'skuManagement/startDataProfileRecalc',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('sku-management/data-profile/recalculate');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || body.error || 'Could not start data profile recalculation',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('startDataProfileRecalcApi', error, rejectWithValue);
    }
  },
);

export const getSkuItemsPageApi = createAsyncThunk(
  'skuManagement/getItemsPage',
  async (params, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') {
          return;
        }
        searchParams.set(k, String(v));
      });
      const qs = searchParams.toString();
      const url = qs ? `sku-management/items?${qs}` : 'sku-management/items';
      const response = await api.get(url);
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || 'Could not load SKU items',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getSkuItemsPageApi', error, rejectWithValue);
    }
  },
);

export const updateSkuItemCatalogApi = createAsyncThunk(
  'skuManagement/updateItemCatalog',
  async ({ skuMongoId, body }, { rejectWithValue }) => {
    try {
      const response = await api.put(`sku-management/items/${skuMongoId}/catalog`, body);
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not update catalog',
        });
      }
      return res.data;
    } catch (error) {
      return handleApiError('updateSkuItemCatalogApi', error, rejectWithValue);
    }
  },
);

export const previewExternalListingForSkuApi = createAsyncThunk(
  'skuManagement/previewExternalListing',
  async ({ skuMongoId, ebayItemId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `sku-management/items/${skuMongoId}/preview-external-listing`,
        { ebayItemId },
      );
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not load listing',
        });
      }
      return res.data;
    } catch (error) {
      return handleApiError('previewExternalListingForSkuApi', error, rejectWithValue);
    }
  },
);

export const applyCopyFromExternalListingApi = createAsyncThunk(
  'skuManagement/applyCopyFromListing',
  async ({ skuMongoId, body }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `sku-management/items/${skuMongoId}/apply-copy-from-listing`,
        body,
      );
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not apply copy',
        });
      }
      return { message: res.message, ...res.data };
    } catch (error) {
      return handleApiError('applyCopyFromExternalListingApi', error, rejectWithValue);
    }
  },
);

/** Pushes current catalog data to linked eBay listings (no catalog edit). */
export const resyncSkuItemListingsApi = createAsyncThunk(
  'skuManagement/resyncItemListings',
  async ({ skuMongoId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`sku-management/items/${skuMongoId}/resync-listings`);
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not resync listings',
        });
      }
      return { message: res.message, ...res.data };
    } catch (error) {
      return handleApiError('resyncSkuItemListingsApi', error, rejectWithValue);
    }
  },
);

export const getPublishToStoreEligibleStoresApi = createAsyncThunk(
  'skuManagement/getPublishToStoreEligibleStores',
  async (skuMongoId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `sku-management/items/${skuMongoId}/publish-to-store/eligible-stores`,
      );
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not load eligible stores',
        });
      }
      return res.data ?? res;
    } catch (error) {
      return handleApiError('getPublishToStoreEligibleStoresApi', error, rejectWithValue);
    }
  },
);

export const publishSkuToStoreApi = createAsyncThunk(
  'skuManagement/publishSkuToStore',
  async ({ skuMongoId, storeId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`sku-management/items/${skuMongoId}/publish-to-store`, {
        storeId,
      });
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not create listing on store',
        });
      }
      return res.data ?? res;
    } catch (error) {
      return handleApiError('publishSkuToStoreApi', error, rejectWithValue);
    }
  },
);

/** Saves catalog chunk + pushes title / images / specs / fitment to linked eBay listings. */
export const deleteSkuItemApi = createAsyncThunk(
  'skuManagement/deleteSkuItem',
  async ({ skuMongoId, endMarketplaceListings = true }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `sku-management/items/${skuMongoId}?endMarketplaceListings=${endMarketplaceListings}`,
      );
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not delete SKU',
        });
      }
      return res.data ?? res;
    } catch (error) {
      return handleApiError('deleteSkuItemApi', error, rejectWithValue);
    }
  },
);

export const bulkDeleteSkuItemsApi = createAsyncThunk(
  'skuManagement/bulkDeleteSkuItems',
  async ({ skus, file, endMarketplaceListings = true }, { rejectWithValue }) => {
    try {
      let response;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('endMarketplaceListings', String(endMarketplaceListings));
        response = await api.post('sku-management/items/bulk-delete', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await api.post('sku-management/items/bulk-delete', {
          skus,
          endMarketplaceListings,
        });
      }
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Bulk delete failed',
        });
      }
      return res.data ?? res;
    } catch (error) {
      return handleApiError('bulkDeleteSkuItemsApi', error, rejectWithValue);
    }
  },
);

export const deleteSkuListingApi = createAsyncThunk(
  'skuManagement/deleteSkuListing',
  async ({ skuMongoId, listingId, endOnMarketplace = true }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `sku-management/items/${skuMongoId}/listings/${listingId}?endOnMarketplace=${endOnMarketplace}`,
      );
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not delete listing',
        });
      }
      return res.data ?? res;
    } catch (error) {
      return handleApiError('deleteSkuListingApi', error, rejectWithValue);
    }
  },
);

export const bulkDeleteListingsApi = createAsyncThunk(
  'skuManagement/bulkDeleteListings',
  async ({ storeId, externalPlatformIds, endOnMarketplace = true }, { rejectWithValue }) => {
    try {
      const response = await api.post('sku-management/items/bulk-delete-listings', {
        storeId,
        externalPlatformIds,
        endOnMarketplace,
      });
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Bulk listing delete failed',
        });
      }
      return res.data ?? res;
    } catch (error) {
      return handleApiError('bulkDeleteListingsApi', error, rejectWithValue);
    }
  },
);

export async function startSkuItemsExportApi(query = {}) {
  const params = buildItemsExportQueryParams(query);
  const response = await api.post(`sku-management/items/export/start?${params.toString()}`);
  const body = response?.data;
  if (!body?.success) {
    throw new Error(body?.message || body?.error || 'Could not start export');
  }
  return body.data ?? body;
}

export async function getSkuItemsExportStatusApi() {
  const response = await api.get('sku-management/items/export/status');
  const body = response?.data;
  if (!body?.success) {
    throw new Error(body?.message || body?.error || 'Could not get export status');
  }
  return body.data ?? body;
}

export async function downloadSkuItemsExportResultApi() {
  const response = await api.get('sku-management/items/export/download', {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  link.setAttribute('download', `sku-items-export-${stamp}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return { downloaded: true };
}

function buildItemsExportQueryParams(query = {}) {
  const params = new URLSearchParams();
  if (query.search) params.append('search', query.search);
  if (query.searchField) params.append('searchField', query.searchField);
  if (query.health && query.health !== 'all') params.append('health', query.health);
  if (query.storeId) params.append('storeId', query.storeId);
  if (query.supplierId) params.append('supplierId', query.supplierId);
  if (query.dataProfile && query.dataProfile !== 'all') {
    params.append('dataProfile', query.dataProfile);
  }
  if (query.marketplace) params.append('marketplace', query.marketplace);
  if (query.priceMin != null) params.append('priceMin', String(query.priceMin));
  if (query.priceMax != null) params.append('priceMax', String(query.priceMax));
  if (query.quantityMin != null) params.append('quantityMin', String(query.quantityMin));
  if (query.quantityMax != null) params.append('quantityMax', String(query.quantityMax));
  return params;
}

export async function downloadSkuItemsExportCsvApi(query = {}) {
  await startSkuItemsExportApi(query);
  const deadline = Date.now() + 120000;
  while (Date.now() < deadline) {
    const status = await getSkuItemsExportStatusApi();
    if (status?.error) {
      throw new Error(status.error);
    }
    if (status?.ready && !status?.isRunning) {
      return downloadSkuItemsExportResultApi();
    }
    await new Promise((r) => setTimeout(r, 350));
  }
  throw new Error('Export timed out');
}

export const downloadSkuDeleteReportCsvApi = createAsyncThunk(
  'skuManagement/downloadDeleteReportCsv',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('sku-management/delete-report/download', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `sku-delete-report-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { downloaded: true };
    } catch (error) {
      return handleApiError('downloadSkuDeleteReportCsvApi', error, rejectWithValue);
    }
  },
);

export const updateSkuItemAttributesApi = createAsyncThunk(
  'skuManagement/updateItemAttributes',
  async ({ skuMongoId, body }, { rejectWithValue }) => {
    try {
      const response = await api.put(`sku-management/items/${skuMongoId}/attributes`, body);
      const res = response.data;
      if (!res.success) {
        return rejectWithValue({
          message: res.message || res.error || 'Could not update attributes',
        });
      }
      return res.data;
    } catch (error) {
      return handleApiError('updateSkuItemAttributesApi', error, rejectWithValue);
    }
  },
);
